import React, { useState, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Grid,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Stack,
} from '@mui/material'
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Delete as DeleteIcon,
  Warning as WarnIcon,
  Block as BanIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material'
import {
  useGetModerationQueueQuery,
  useGetModerationStatsQuery,
  useTakeModerationActionMutation,
  useBulkModerationActionMutation,
  ModerationItem,
  ModerationActionType,
  ModerationStatus,
  ModerationContentType,
  ModerationSeverity,
} from '@store/api/adminApi'
import AdminPageGate from '@components/auth/AdminPageGate'
import { ResponsiveTable } from '@components/layout/ResponsiveTable'

const SEVERITY_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  low: 'success',
  medium: 'warning',
  high: 'error',
  critical: 'error',
}

const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  pending: 'warning',
  reviewed: 'info',
  resolved: 'success',
  dismissed: 'default',
}

interface ActionDialogState {
  open: boolean
  item: ModerationItem | null
  action: ModerationActionType | null
}

const AdminModeration: React.FC = () => {
  // Filters
  const [tab, setTab] = useState<ModerationContentType | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<ModerationStatus | ''>('')
  const [severityFilter, setSeverityFilter] = useState<ModerationSeverity | ''>('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)

  // Selection
  const [selected, setSelected] = useState<string[]>([])

  // Action dialog
  const [actionDialog, setActionDialog] = useState<ActionDialogState>({ open: false, item: null, action: null })
  const [actionReason, setActionReason] = useState('')

  // Bulk action dialog
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false)
  const [bulkAction, setBulkAction] = useState<ModerationActionType>('dismiss')
  const [bulkReason, setBulkReason] = useState('')

  const queryParams = {
    ...(tab !== 'all' && { contentType: tab }),
    ...(statusFilter && { status: statusFilter }),
    ...(severityFilter && { severity: severityFilter }),
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo }),
    page: page + 1,
    limit: rowsPerPage,
  }

  const { data: queueData, isLoading, error, refetch } = useGetModerationQueueQuery(queryParams)
  const { data: statsData } = useGetModerationStatsQuery()
  const [takeAction, { isLoading: actionLoading }] = useTakeModerationActionMutation()
  const [bulkActionMutation, { isLoading: bulkLoading }] = useBulkModerationActionMutation()

  const items: ModerationItem[] = useMemo(
    () => (queueData?.data as ModerationItem[]) ?? [],
    [queueData?.data],
  )
  const total = (queueData?.meta as any)?.pagination?.total ?? 0
  const stats = statsData?.data

  const handleTabChange = (_: React.SyntheticEvent, value: ModerationContentType | 'all') => {
    setTab(value)
    setPage(0)
    setSelected([])
  }

  const handleSelectAll = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(items.map((item) => item.id))
    } else {
      setSelected([])
    }
  }, [items])

  const handleSelectOne = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const openActionDialog = useCallback((item: ModerationItem, action: ModerationActionType) => {
    setActionDialog({ open: true, item, action })
    setActionReason('')
  }, [])

  const closeActionDialog = () => {
    setActionDialog({ open: false, item: null, action: null })
    setActionReason('')
  }

  const confirmAction = async () => {
    if (!actionDialog.item || !actionDialog.action) return
    await takeAction({ id: actionDialog.item.id, action: actionDialog.action, reason: actionReason })
    closeActionDialog()
  }

  const confirmBulkAction = async () => {
    if (selected.length === 0) return
    await bulkActionMutation({ ids: selected, action: bulkAction, reason: bulkReason })
    setSelected([])
    setBulkDialogOpen(false)
    setBulkReason('')
  }

  const moderationColumns = useMemo(
    () => [
      {
        id: 'select',
        header: (
          <Checkbox
            indeterminate={selected.length > 0 && selected.length < items.length}
            checked={items.length > 0 && selected.length === items.length}
            onChange={handleSelectAll}
          />
        ),
        cardLabel: 'Select',
        renderCell: (item: ModerationItem) => (
          <Checkbox checked={selected.includes(item.id)} onChange={() => handleSelectOne(item.id)} />
        ),
      },
      {
        id: 'type',
        header: 'Type',
        renderCell: (item: ModerationItem) => (
          <Chip label={item.contentType} size="small" variant="outlined" />
        ),
      },
      {
        id: 'excerpt',
        header: 'Excerpt',
        renderCell: (item: ModerationItem) => (
          <Typography variant="body2" noWrap title={item.contentExcerpt}>
            {item.contentExcerpt || <em>No preview</em>}
          </Typography>
        ),
      },
      {
        id: 'reporter',
        header: 'Reporter',
        renderCell: (item: ModerationItem) => item.reporter?.username ?? '—',
      },
      {
        id: 'reason',
        header: 'Reason',
        renderCell: (item: ModerationItem) => (
          <Typography variant="body2" noWrap title={item.reason}>{item.reason}</Typography>
        ),
      },
      {
        id: 'severity',
        header: 'Severity',
        renderCell: (item: ModerationItem) => (
          <Chip label={item.severity} size="small" color={SEVERITY_COLORS[item.severity] ?? 'default'} />
        ),
      },
      {
        id: 'status',
        header: 'Status',
        renderCell: (item: ModerationItem) => (
          <Chip label={item.status} size="small" color={STATUS_COLORS[item.status] ?? 'default'} />
        ),
      },
      {
        id: 'date',
        header: 'Date',
        renderCell: (item: ModerationItem) => (
          <Typography variant="caption">{new Date(item.createdAt).toLocaleDateString()}</Typography>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        renderCell: (item: ModerationItem) => (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => openActionDialog(item, 'approve')}><ApproveIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Reject"><IconButton size="small" color="error" onClick={() => openActionDialog(item, 'reject')}><RejectIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Delete content"><IconButton size="small" onClick={() => openActionDialog(item, 'delete')}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Warn user"><IconButton size="small" color="warning" onClick={() => openActionDialog(item, 'warn')}><WarnIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Ban user"><IconButton size="small" color="error" onClick={() => openActionDialog(item, 'ban')}><BanIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Dismiss"><IconButton size="small" onClick={() => openActionDialog(item, 'dismiss')}><ViewIcon fontSize="small" /></IconButton></Tooltip>
          </Stack>
        ),
      },
    ],
    [items, selected, openActionDialog, handleSelectAll],
  )

  return (
    <AdminPageGate permission="content:read" title="Moderation Queue">
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h4">Moderation Queue</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={refetch}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      {/* Stats cards */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          {Object.entries(stats.byStatus ?? {}).map(([status, count]) => (
            <Grid item xs={6} sm={3} key={status}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" textTransform="capitalize">{status}</Typography>
                  <Typography variant="h4">{count as number}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tabs by content type */}
      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="All" value="all" />
        <Tab label="Posts" value="post" />
        <Tab label="Comments" value="comment" />
        <Tab label="Reviews" value="review" />
        <Tab label="Users" value="user" />
      </Tabs>

      {/* Filters */}
      <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap">
        <FilterIcon color="action" />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value as ModerationStatus | ''); setPage(0) }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="reviewed">Reviewed</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="dismissed">Dismissed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Severity</InputLabel>
          <Select value={severityFilter} label="Severity" onChange={(e) => { setSeverityFilter(e.target.value as ModerationSeverity | ''); setPage(0) }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="From date"
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(0) }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="To date"
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(0) }}
          InputLabelProps={{ shrink: true }}
        />
        {(statusFilter || severityFilter || dateFrom || dateTo) && (
          <Button size="small" onClick={() => { setStatusFilter(''); setSeverityFilter(''); setDateFrom(''); setDateTo(''); setPage(0) }}>
            Clear
          </Button>
        )}
      </Stack>

      {/* Bulk actions bar */}
      {selected.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setBulkDialogOpen(true)}>
              Bulk Action ({selected.length} selected)
            </Button>
          }
        >
          {selected.length} item(s) selected
        </Alert>
      )}

      {/* Queue table */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">Failed to load moderation queue.</Alert>
      ) : (
        <Box>
          <ResponsiveTable
            rows={items}
            columns={moderationColumns}
            getRowId={(item) => item.id}
            expandTableOnMobile
            emptyMessage="No items in queue"
            data-testid="admin-moderation-list"
          />
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Box>
      )}

      {/* Single action dialog */}
      <Dialog open={actionDialog.open} onClose={closeActionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionDialog.action && `${actionDialog.action.charAt(0).toUpperCase() + actionDialog.action.slice(1)} — ${actionDialog.item?.contentType}`}
        </DialogTitle>
        <DialogContent>
          {actionDialog.item?.contentExcerpt && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">{actionDialog.item.contentExcerpt}</Typography>
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (optional)"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog}>Cancel</Button>
          <Button variant="contained" onClick={confirmAction} disabled={actionLoading}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk action dialog */}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Action ({selected.length} items)</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Action</InputLabel>
            <Select value={bulkAction} label="Action" onChange={(e) => setBulkAction(e.target.value as ModerationActionType)}>
              <MenuItem value="approve">Approve</MenuItem>
              <MenuItem value="reject">Reject</MenuItem>
              <MenuItem value="delete">Delete</MenuItem>
              <MenuItem value="warn">Warn user</MenuItem>
              <MenuItem value="ban">Ban user</MenuItem>
              <MenuItem value="dismiss">Dismiss</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (optional)"
            value={bulkReason}
            onChange={(e) => setBulkReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmBulkAction} disabled={bulkLoading}>
            Apply to {selected.length} items
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </AdminPageGate>
  )
}

export default AdminModeration
